import { FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { FlexContainer } from '@starjack/shared-components';
import { LoadingButton } from '@mui/lab';
import { usePostTicketCancellationMutation } from '../../api/ticket.api';
import { useNotification } from '@starjack/features/notifications';
import { Ticket } from '../../model/ticket.types';
import { TicketDrawerLabel } from '../../ui/ticketDrawerContent/ticketDrawerLabel';
import { formatDateToString } from '../../lib/ticketUiUtils';

type ConfirmationDialogConfig = {
    open: boolean;
    type: 'refund' | 'cancel';
};

export const TicketRefundBlock: FunctionComponent<{ ticket: Ticket }> = ({
    ticket,
}) => {
    const { t } = useTranslation();
    const [confirmationDialogConfig, setConfirmationDialog] =
        useState<ConfirmationDialogConfig>({ open: false, type: 'refund' });

    const [postCancelTicket, { isLoading: isLoading }] =
        usePostTicketCancellationMutation();
    const { addNotification } = useNotification();

    const onDeleteTicket = () => {
        postCancelTicket({
            ticketId: ticket.id,
            refund: confirmationDialogConfig.type === 'refund',
        })
            .unwrap()
            .then(() => {
                addNotification({
                    message:
                        confirmationDialogConfig.type === 'refund'
                            ? t('refundBlock.notifications.successRefund')
                            : t('refundBlock.notifications.successCancel'),
                    type: 'success',
                });
                setConfirmationDialog((prevState) => ({
                    ...prevState,
                    open: false,
                }));
            })
            .catch(() => {
                addNotification({
                    message:
                        confirmationDialogConfig.type === 'refund'
                            ? t('refundBlock.notifications.errorRefund')
                            : t('refundBlock.notifications.errorCancel'),
                    type: 'error',
                });
            });
    };

    const isCancelable =
        ticket.cancelable && !ticket.cancelledBy && !ticket.cancellationDate;

    const notCancelableString = useMemo(() => {
        if (ticket.cancelledBy || ticket.cancellationDate) {
            return (
                <Box
                    sx={{
                        width: '100%',
                        textAlign: 'center',
                    }}
                >
                    <TicketDrawerLabel>
                        {t('refundBlock.alreadyCancelled')}
                    </TicketDrawerLabel>

                    <TicketDrawerLabel>
                        {t('refundBlock.cancelledBy')}{' '}
                        <strong>{ticket.cancelledBy}</strong>
                    </TicketDrawerLabel>
                    <TicketDrawerLabel>
                        {t('refundBlock.cancelledOn')}{' '}
                        <strong>
                            {formatDateToString(ticket.cancellationDate, {
                                detailed: true,
                            })}
                        </strong>
                    </TicketDrawerLabel>
                </Box>
            );
        } else if (!ticket.cancelable) {
            return (
                <TicketDrawerLabel>
                    {ticket.periodLocked
                        ? t('refundBlock.periodLocked')
                        : t('refundBlock.notCancelable')}
                </TicketDrawerLabel>
            );
        }
        return '';
    }, [
        ticket.cancelable,
        ticket.cancelledBy,
        ticket.cancellationDate,
        ticket.periodLocked,
    ]);

    return (
        <Box>
            <FlexContainer
                sx={{
                    gap: 2,
                }}
                center
            >
                {!isCancelable ? (
                    <TicketDrawerLabel
                        sx={{
                            fontWeight: 600,
                        }}
                    >
                        {notCancelableString}
                    </TicketDrawerLabel>
                ) : (
                    <>
                        <Button
                            onClick={() => {
                                setConfirmationDialog(() => ({
                                    open: true,
                                    type: 'refund',
                                }));
                            }}
                            variant='contained'
                            color='error'
                        >
                            {t('refundBlock.refundButton')}
                        </Button>

                        <Button
                            onClick={() => {
                                setConfirmationDialog(() => ({
                                    open: true,
                                    type: 'cancel',
                                }));
                            }}
                            variant='contained'
                            color='error'
                        >
                            {t('refundBlock.cancelButton')}
                        </Button>
                    </>
                )}
            </FlexContainer>

            <Dialog
                onClose={() => {
                    setConfirmationDialog((prevState) => ({
                        ...prevState,
                        open: false,
                    }));
                }}
                open={confirmationDialogConfig.open}
            >
                <DialogTitle>
                    {confirmationDialogConfig.type === 'refund'
                        ? t('refundBlock.dialogTitle.refund')
                        : t('refundBlock.dialogTitle.cancel')}
                </DialogTitle>

                <DialogContent>
                    {confirmationDialogConfig.type === 'refund'
                        ? t('refundBlock.dialogContent.refund')
                        : t('refundBlock.dialogContent.cancel')}
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => {
                            setConfirmationDialog((prevState) => ({
                                ...prevState,
                                open: false,
                            }));
                        }}
                        variant='outlined'
                    >
                        {t('refundBlock.dialogActions.cancel')}
                    </Button>
                    <LoadingButton
                        loading={isLoading}
                        onClick={onDeleteTicket}
                        color='error'
                        variant='contained'
                    >
                        {confirmationDialogConfig.type === 'refund'
                            ? t('refundBlock.dialogActions.refund')
                            : t('refundBlock.dialogActions.confirmCancel')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
